import { EventItem, TimeSlot } from './types.ts';

export const PRIME_TIME_SLOTS: TimeSlot[] = ['Noon', 'Afternoon', 'Evening'];
export const ALL_TIME_SLOTS: TimeSlot[] = ['Morning', 'Noon', 'Afternoon', 'Evening'];

/**
 * Maps an ISO 8601 start date string to its time slot of the day.
 * - Morning: 05:00 - 11:59 (hours < 12)
 * - Noon: 12:00 - 14:29 (hours 12..14.5)
 * - Afternoon: 14:30 - 17:59 (hours 14.5..18)
 * - Evening: 18:00 - 04:59 (hours >= 18 or < 5)
 */
export function getTimeSlot(startIso: string): TimeSlot {
  const date = new Date(startIso);
  if (isNaN(date.getTime())) return 'Afternoon';
  const hours = date.getHours() + date.getMinutes() / 60;
  if (hours >= 5 && hours < 12) return 'Morning';
  if (hours >= 12 && hours < 14.5) return 'Noon';
  if (hours >= 14.5 && hours < 18) return 'Afternoon';
  return 'Evening';
}

export interface DailyTimeSlotSummary {
  dateStr: string; // "YYYY-MM-DD"
  formattedDate: string; // "Mon, Sep 28"
  slots: Record<TimeSlot, {
    count: number;
    spots: number;
    events: EventItem[];
  }>;
  filledPrimeSlotsCount: number; // 0..3 (Noon, Afternoon, Evening)
  primeCoveragePercent: number; // (filledPrimeSlotsCount / 3) * 100
  missingPrimeSlots: TimeSlot[]; // e.g. ['Evening']
}

export interface TimeSlotAnalysisSummary {
  totalActiveDays: number;
  totalPrimeSlotsAvailable: number; // totalActiveDays * 3
  totalPrimeSlotsFilled: number;
  primeTimeCoverageScore: number; // percentage (0..100)
  fullyCoveredDaysCount: number;
  partiallyCoveredDaysCount: number;
  daysSummary: DailyTimeSlotSummary[];
  slotTotals: Record<TimeSlot, { count: number; spots: number }>;
}

export function calculateTimeSlotAnalysis(
  events: EventItem[],
  startDateStr: string = '2026-09-28',
  numDays: number = 14
): TimeSlotAnalysisSummary {
  // Collect target dates (default 14 days of O-Weeks)
  const dateSet = new Set<string>();
  const baseDate = new Date(`${startDateStr}T00:00:00`);

  for (let i = 0; i < numDays; i++) {
    const d = new Date(baseDate.getTime() + i * 86400000);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    dateSet.add(`${yyyy}-${mm}-${dd}`);
  }

  // Also add any event dates outside default range
  for (const ev of events) {
    if (ev.start) {
      const dStr = ev.start.split('T')[0];
      if (dStr) dateSet.add(dStr);
    }
  }

  const sortedDates = Array.from(dateSet).sort();

  const slotTotals: Record<TimeSlot, { count: number; spots: number }> = {
    Morning: { count: 0, spots: 0 },
    Noon: { count: 0, spots: 0 },
    Afternoon: { count: 0, spots: 0 },
    Evening: { count: 0, spots: 0 },
  };

  let totalPrimeSlotsFilled = 0;
  let fullyCoveredDaysCount = 0;
  let partiallyCoveredDaysCount = 0;

  const daysSummary: DailyTimeSlotSummary[] = sortedDates.map(dateStr => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const dObj = new Date(year, month - 1, day);
    const formattedDate = dObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });

    const dayEvents = events.filter(e => e.start && e.start.startsWith(dateStr));

    const slots: Record<TimeSlot, { count: number; spots: number; events: EventItem[] }> = {
      Morning: { count: 0, spots: 0, events: [] },
      Noon: { count: 0, spots: 0, events: [] },
      Afternoon: { count: 0, spots: 0, events: [] },
      Evening: { count: 0, spots: 0, events: [] },
    };

    for (const ev of dayEvents) {
      const slot = getTimeSlot(ev.start);
      const spots = Number(ev.capacity) || 0;
      slots[slot].count += 1;
      slots[slot].spots += spots;
      slots[slot].events.push(ev);

      slotTotals[slot].count += 1;
      slotTotals[slot].spots += spots;
    }

    let filledPrimeSlotsCount = 0;
    const missingPrimeSlots: TimeSlot[] = [];

    for (const primeSlot of PRIME_TIME_SLOTS) {
      if (slots[primeSlot].count > 0) {
        filledPrimeSlotsCount++;
      } else {
        missingPrimeSlots.push(primeSlot);
      }
    }

    const primeCoveragePercent = (filledPrimeSlotsCount / 3) * 100;
    totalPrimeSlotsFilled += filledPrimeSlotsCount;

    if (filledPrimeSlotsCount === 3) {
      fullyCoveredDaysCount++;
    } else {
      partiallyCoveredDaysCount++;
    }

    return {
      dateStr,
      formattedDate,
      slots,
      filledPrimeSlotsCount,
      primeCoveragePercent,
      missingPrimeSlots,
    };
  });

  const totalActiveDays = daysSummary.length;
  const totalPrimeSlotsAvailable = totalActiveDays * 3;
  const primeTimeCoverageScore = totalPrimeSlotsAvailable > 0
    ? (totalPrimeSlotsFilled / totalPrimeSlotsAvailable) * 100
    : 0;

  return {
    totalActiveDays,
    totalPrimeSlotsAvailable,
    totalPrimeSlotsFilled,
    primeTimeCoverageScore,
    fullyCoveredDaysCount,
    partiallyCoveredDaysCount,
    daysSummary,
    slotTotals,
  };
}
